import {
  type LDContext,
  LDProvider,
  useLDClient,
} from "launchdarkly-react-client-sdk";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAuth, type AuthContextType } from "./AuthProvider";

type LaunchDarklyStatus = { isReady: boolean };
export type LaunchDarklyAuthState = Pick<
  AuthContextType,
  "isAuthenticated" | "isLoading" | "user"
>;

const LaunchDarklyStatusContext = createContext<LaunchDarklyStatus>({
  isReady: true,
});

function LDContextIdentifier({
  children,
  context,
  isReadyToIdentify,
}: {
  children: ReactNode;
  context: LDContext;
  isReadyToIdentify: boolean;
}) {
  const ldClient = useLDClient();
  const [isReady, setIsReady] = useState(false);
  const lastIdentifiedContextKeyRef = useRef<string | null>(null);
  const contextKey = JSON.stringify(context);

  useEffect(() => {
    if (!ldClient) {
      setIsReady(false);
      return;
    }

    if (!isReadyToIdentify) {
      setIsReady(false);
      lastIdentifiedContextKeyRef.current = null;
      return;
    }

    if (lastIdentifiedContextKeyRef.current === contextKey) {
      setIsReady(true);
      return;
    }

    let isCancelled = false;
    setIsReady(false);
    lastIdentifiedContextKeyRef.current = contextKey;

    Promise.resolve(ldClient.identify(context))
      .catch(() => {
        lastIdentifiedContextKeyRef.current = null;
      })
      .finally(() => {
        if (!isCancelled) {
          setIsReady(true);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [context, contextKey, isReadyToIdentify, ldClient]);

  return (
    <LaunchDarklyStatusContext.Provider value={{ isReady }}>
      {children}
    </LaunchDarklyStatusContext.Provider>
  );
}

export type AuthLaunchDarklyProviderProps = {
  children: ReactNode;
  clientSideID?: string;
  getContext?: (auth: LaunchDarklyAuthState) => LDContext;
  isReadyToIdentify?: (auth: LaunchDarklyAuthState) => boolean;
};

export function useLaunchDarklyStatus() {
  return useContext(LaunchDarklyStatusContext);
}

export function AuthLaunchDarklyProvider({
  children,
  clientSideID,
  getContext = ({ user }) => ({
    kind: "user",
    key: user?.sub ?? "anonymous",
    email: user?.email ?? "",
    role: user?.role ?? "",
    roles: user?.roles ?? [],
    cognitoGroups: user?.cognitoGroups ?? [],
  }),
  isReadyToIdentify = ({ isAuthenticated, isLoading, user }) =>
    !isLoading && isAuthenticated && Boolean(user?.sub) && Boolean(user?.email),
}: AuthLaunchDarklyProviderProps) {
  const auth = useAuth();

  if (!clientSideID) {
    return (
      <LaunchDarklyStatusContext.Provider value={{ isReady: true }}>
        {children}
      </LaunchDarklyStatusContext.Provider>
    );
  }

  const context = getContext(auth);
  const anonymousContext: LDContext = {
    kind: "user",
    key: "anonymous",
    anonymous: true,
  };

  return (
    <LDProvider clientSideID={clientSideID} context={anonymousContext}>
      <LDContextIdentifier
        context={context}
        isReadyToIdentify={isReadyToIdentify(auth)}
      >
        {children}
      </LDContextIdentifier>
    </LDProvider>
  );
}
